const reISO = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*))(?:Z|(\+|-)([\d|:]*))?$/;
const reMsAjax = /^\/Date\((d|-|.*)\)[/|\\]$/;

export default {
    /**
     * Parses string dates (from service result JSON that is in ISO or MS Ajax format) to JS Dates.
     *
     * @param {*} key Property name that is parsed.
     * @param {*} value Property value.
     * @returns Date
     */
    dateParser(key, value) {
        if (typeof value === 'string') {
            let a = reISO.exec(value);
            if (a) return new Date(value);
            a = reMsAjax.exec(value);
            if (a) {
                const b = a[1].split(/[-+,.]/);
                return new Date(b[0] ? +b[0] : 0 - +b[1]);
            }
        }
        return value;
    },

    /**
     * Enforces that a function not be called again until a certain amount of time has passed without it being called, e.g. execute the function only if 100 milliseconds have passed without it being called.
     *
     * @param {*} func Function to limit.
     * @param {*} delay Delay in ms.
     * @returns Function
     */
    debounce(func, delay) {
        let inDebounce;
        return function noName(...args) {
            const context = this;
            // const args = arguments
            clearTimeout(inDebounce);
            inDebounce = setTimeout(() => func.apply(context, args), delay);
        };
    },

    /**
     * Shallow field comparison between two objects.
     * Returns the changed fields of oUpdated if different from oSource.
     * Overlapping fields from oSource are returned if overlap is true.
     *
     * @param {*} oSource Master object to compare against.
     * @param {*} oUpdated Updated object and values to test.
     * @param {*} overlap If a boolean, indicates that overlapping fields from source must be returned, otherwise it can be the master object fields.
     * @returns Object
     */
    delta(oSource, oUpdated, overlap) {
        const delta = {};
        if (!oSource || !oUpdated) return null;
        let keys;
        if (typeof overlap === 'object') keys = Object.keys(overlap);
        else if (overlap === true) keys = Object.keys(oSource);
        else keys = Object.keys(oUpdated);
        const len = keys.length;
        if (len === 0) return null;
        keys.forEach(key => { // Check each property.
            if (oUpdated[key] !== oSource[key]) { // Check strict value.
                delta[key] = oUpdated[key]; // Value is different. Add to delta.
            }
        });

        if (Object.keys(delta).length === 0) return null; // No changed fields.
        delta._id = oSource._id;
        return delta;
    },

    /**
     * Can clone anything by using JSON parse and stringify. Deep copy.
     * Slow but makes a full deep copy.
     *
     * @param {any} o Object to duplicate.
     * @returns Duplicated object.
     */
    duplicate(o) {
        return o ? JSON.parse(JSON.stringify(o)) : {};
    },

    /**
     * Sort an object array by field.
     *
     * @param {*} arr Array to sort.
     * @param {*} field Field to sort on.
     */
    sort(arr, field) {
        if (!arr.length) return;
        if (isNaN(arr[0][field])) { // Text sort.
            arr.sort((a, b) => {
                const aV = (a[field] || '').toUpperCase();
                const bV = (b[field] || '').toUpperCase();
                if (aV < bV) return -1;
                if (aV > bV) return 1;
                return 0;
            });
        }
        else { // Numeric sort (10 or "10" is a valid numeric)
            arr.sort((a, b) => {
                const aV = a[field] || a || 0;
                const bV = b[field] || b || 0;
                return aV - bV;
            });
        }
    },

    /**
     * Limit the amount a function is invoked.
     * Enforces a maximum number of times a function can be called over time, e.g. execute the function at most once every n milliseconds.
     *
     * @param {*} func Function to limit.
     * @param {*} limit Time limit in ms.
     * @returns Function
     */
    throttle(func, limit) {
        let lastFunc;
        let lastRan;
        return function noName(...args) {
            const context = this;
            // const args = arguments;
            if (!lastRan) {
                func.apply(context, args);
                lastRan = Date.now();
            }
            else {
                clearTimeout(lastFunc);
                lastFunc = setTimeout(() => {
                    if ((Date.now() - lastRan) >= limit) {
                        func.apply(context, args);
                        lastRan = Date.now();
                    }
                }, limit - (Date.now() - lastRan));
            }
        };
    }
};



// WEBPACK FOOTER //
// ./src/util/Data.js